import { useEffect, useMemo, useRef } from 'react';
import { dayLabel, timeLabel, type TourStrings } from './strings';

/** A slot as the server offers it: when, and free or taken. */
export interface PickerSlot {
  date: string;
  start: string;
  end: string;
  free: boolean;
}

export function groupByDay<T extends PickerSlot>(slots: T[]): { date: string; slots: T[]; anyFree: boolean }[] {
  const days = new Map<string, T[]>();
  for (const sl of slots) days.set(sl.date, [...(days.get(sl.date) ?? []), sl]);
  return [...days.entries()].map(([date, list]) => ({ date, slots: list, anyFree: list.some((x) => x.free) }));
}

/**
 * One question at a time: which day (a row of chips), then which time (a grid).
 * Shared by the public tour page and the signed-in portal's tour sheet.
 * `currentStart` marks the person's own tour (moving it): shown, not pickable.
 */
export function SlotPicker({
  slots,
  timeZone,
  s,
  day,
  onDay,
  onPick,
  currentStart,
  currentLabel,
  selected,
}: {
  slots: PickerSlot[];
  timeZone: string;
  s: TourStrings;
  day: string | null;
  onDay: (date: string) => void;
  onPick: (start: string) => void;
  currentStart?: string | null;
  currentLabel?: string;
  /** The time picked but not yet confirmed (the portal's sheet confirms below the grid). */
  selected?: string | null;
}) {
  const days = useMemo(() => groupByDay(slots), [slots]);
  const daySlots = days.find((d) => d.date === day)?.slots ?? [];
  // The chosen day may sit past the row's edge (moving a tour a week ahead): bring it into view.
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [day]);
  return (
    <>
      <fieldset className="mb-6 min-w-0">
        <legend className="mb-2 text-[17px] font-bold text-brand-900">{s.chooseDay}</legend>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2" role="radiogroup" aria-label={s.chooseDay}>
          {days.map((d) => {
            const active = d.date === day;
            return (
              <button
                key={d.date}
                ref={active ? activeRef : undefined}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={!d.anyFree}
                onClick={() => onDay(d.date)}
                className={
                  'flex min-h-[56px] min-w-[96px] shrink-0 flex-col items-center justify-center rounded-xl border px-3 text-[16px] ' +
                  (active
                    ? 'border-brand-600 bg-brand-600 font-semibold text-white'
                    : d.anyFree
                      ? 'border-slate-200 bg-white text-slate-800'
                      : 'border-slate-100 bg-slate-50 text-slate-400')
                }
              >
                <span>{dayLabel(d.slots[0]!.start, timeZone, s)}</span>
                {!d.anyFree && <span>{s.full}</span>}
              </button>
            );
          })}
        </div>
      </fieldset>

      {day && (
        <fieldset className="mb-6 min-w-0">
          <legend className="mb-2 text-[17px] font-bold text-brand-900">{s.chooseTime}</legend>
          <div className="grid grid-cols-3 gap-2">
            {daySlots.map((x) => {
              const t = timeLabel(x.start, timeZone, s);
              const mine = !!currentStart && x.start === currentStart;
              const picked = !!selected && x.start === selected;
              return (
                <button
                  key={x.start}
                  type="button"
                  disabled={!x.free || mine}
                  aria-label={mine ? `${t} ${currentLabel ?? ''}`.trim() : x.free ? t : `${t} ${s.taken}`}
                  aria-pressed={selected !== undefined ? picked : undefined}
                  onClick={() => onPick(x.start)}
                  className={
                    'min-h-[48px] rounded-xl border text-[16px] ' +
                    (picked
                      ? 'border-brand-600 bg-brand-600 font-semibold text-white'
                      : mine
                        ? 'border-brand-300 bg-brand-50 font-semibold text-brand-700'
                        : x.free
                          ? 'border-slate-200 bg-white font-semibold text-slate-800'
                          : 'border-slate-100 bg-slate-50 text-slate-400 line-through')
                  }
                  dir="ltr"
                >
                  {t}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
    </>
  );
}
